import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import BasicReactTableWrapper from "../../../shared_components/basic_react_table_wrapper";

const DISABLE_SORT = "DISABLE_SORT";
const INVISIBLE = "NOT_USED";

const numberDashBlanks = e => {
  if (typeof e === "undefined") {
    return "—";
  } else if (e === "") {
    return "—";
  } else {
    return e;
  }
};
const boolProcessor = e =>  e ? "Yes" : "No";
const dashBlanks = e => e ? e : "—";
const noop = e => e;
const makeApplicationIcon = (v, e) => {
  const icon = e.applicationIcon;
  const name = e.application;
  let r = name;

  if (icon) {
    r = <img className="tag-icon-image" title={name} data-original-title={name} src={icon} />;
  }

  return r;
};

const purposeSubComponentFieldToHide = {
  "Drinking water quality": [
    "dischargePoint", "plantSize", "treatmentLevel"
  ],
  "Wastewater discharge": [
    "monitoringPoint"
  ]
};

const dataProcessor = columnKeysAndHeadings => e => {
  const keys = Object.keys(columnKeysAndHeadings);

  return keys.map(key => {
    const v = e[key];
    const valueProcessor = columnKeysAndHeadings[key][2];

    if ((purposeSubComponentFieldToHide[e.application] || []).includes(key)) {
      return INVISIBLE;
    } else {
      return valueProcessor(v, e);
    }
  });
};

// Use these for the buttons in the subcomponent
const basePath = "/data-hub/regulations/";
const parameterHref = parameterId => `${basePath}by-parameter/?parameter_id=${parameterId}`;
const regulationHref = regulationId => `${basePath}by-regulation/?regulation_id=${regulationId}`;
const newTabIcon = <i className="fa fa-external-link"></i>;
const jumpToButton = (label, standard) => {
  let href;
  if (label === "regulation") {
    href = regulationHref(standard.regulationId);
  } else if (label === "parameter") {
    href = parameterHref(standard.parameterId);
  } else {
    throw "Unhandled option.";
  }

  return <a target="_blank" onClick={e => e.stopPropagation()} href={href} className="btn btn-primary btn-block jump-to-button">About this {label} {newTabIcon}</a>;
};

class StandardsList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: {}
    };
  }

  componentDidUpdate(prevProps) {
    // Collapse expansions when the parent supplies a new dataset / page / sort
    // (all server-driven). Guarded so our own expand setState does not self-collapse;
    // componentDidUpdate does not fire on mount, matching componentWillReceiveProps.
    if (prevProps.standards !== this.props.standards ||
        prevProps.page !== this.props.page ||
        prevProps.sorted !== this.props.sorted) {
      this.setState({ expanded: {} });
    }
  }

  render () {
    const pageSize = Math.min(this.props.pageSize, this.props.totalResults);
    const totalPages = Math.ceil(this.props.totalResults / pageSize);

    const standards = this.props.standards;

    const columnKeysAndHeadings = {
      countryName:     ["Country",          "country",              noop,                25],
      region:          ["Region",           "region",               dashBlanks,          20],
      parameterName:   ["Parameter",        "regulation_parameter", noop,                25],
      regulationName:  ["Regulation",       "regulation",           noop,                40],
      application:     ["Application",      "purpose",              makeApplicationIcon, 30],
      subApplications: ["Sub-applications", "sub_purposes",         dashBlanks,          15],
      value:           ["Value",            "value",                numberDashBlanks,    25],
      valueRange:      ["Value(range)",      DISABLE_SORT,          dashBlanks,          20],
      valueText:       ["Value(text)",       DISABLE_SORT,          dashBlanks,          20],
      unit:            ["Units",             DISABLE_SORT,          noop,                15],
      notes:           ["Notes",             DISABLE_SORT,          dashBlanks,          30],
      minMaxName:      ["Min/Max",           DISABLE_SORT,          dashBlanks,          15],
      mandatory:       ["Mandatory",        "mandatory",            boolProcessor,       15],
      dateIssued:      ["Year",             "date_issued",          noop,                15],
      current:         ["Current",          "current",              boolProcessor,       15]
    };
    const keys = Object.keys(columnKeysAndHeadings);
    const values = Object.values(columnKeysAndHeadings);
    const minWidths = values.map(e => e[3]);

    const indexOfValue = keys.findIndex(k => k === "value");
    const unsortableColumns = values.map(([name, sortAttr], i) => sortAttr === DISABLE_SORT ? i : false).filter(e => e);

    const data = standards.map(dataProcessor(columnKeysAndHeadings));
    const tableData = [
      values.map(e => e[0])
    ].concat(data);

    // Expandable subcomponent values
    const subComponentColumnKeysAndHeadings = {
      limitType:                ["Limit type",            "limit_type",                         dashBlanks, 10],
      measurementTypeFrequency: ["Measurement frequency", "measurement_type_frequency",         dashBlanks, 10],
      measurementType:          ["Measurement type",      "measurement_type",                   dashBlanks, 10],
      dischargePoint:           ["Discharge point",       "discharge_point",                    dashBlanks, 10],
      monitoringPoint:          ["Monitoring point",      "monitoring_point",                   dashBlanks, 10],
      treatmentLevel:           ["Treatment level",       "treatment_level",                    dashBlanks, 10],
      plantSize:                ["Plant size",            "plant_size",                         dashBlanks, 10],
      otherCategoryGroup:       ["Category group",        "regulation_standard_category_group", dashBlanks, 10],
      otherCategory:            ["Category name",         "regulation_standard_category",       dashBlanks, 10],
      valueTypeLabel:           ["Value type",            "value_type_label",                   dashBlanks, 10],
    };

    const subComponentHeadings = Object.values(subComponentColumnKeysAndHeadings).map(e => e[0]);
    const makeNestedTd = (value, i) => {
      const heading = subComponentHeadings[i];
      const classes = {
        "rt-td": true,
        invisible: value === INVISIBLE
      };

      return (
        <div className={classNames(classes)} key={i}>
          <div className="heading">
            {heading}
          </div>
          <div className="value">
            {value}
          </div>
        </div>
      );
    };
    const subComponent = (row) => {
      const standard = standards[row.index];

      const componentData = dataProcessor(subComponentColumnKeysAndHeadings)(standard);

      const tds = componentData.map(makeNestedTd);
      let parameterButton, regulationButton;
      if (!this.props.parameterFocused) {
        parameterButton = jumpToButton("parameter", standard);
      }
      if (!this.props.regulationFocused) {
        regulationButton = jumpToButton("regulation", standard);
      }

      return (
        <div>
          <div className="rt-tr sub-component regulation-standard-button-wrapper">
            <div className="rt-td">
              {parameterButton}
            </div>
            <div className="rt-td">
              {regulationButton}
            </div>
          </div>
          <div className="rt-tr sub-component">
            {tds}
          </div>
        </div>
      );
    };

    const expandRow = (expandedState, i) => {
      const newExpandedObject = Object.assign({}, this.state.expanded);

      newExpandedObject[i] = !this.state.expanded[i];

      this.setState({ expanded: newExpandedObject });
    };

    const reactTableProps = {
      getTrGroupProps: (state, rowInfo, column) => {
        if (!rowInfo) { return {}; }
        column;

        const expandRowOnClick = () => {
          expandRow(state.expanded, rowInfo.viewIndex);
        };

        return {
          style: {
            background: state.expanded[rowInfo.viewIndex] ? "#E5EFF5" : null
          },
          onClick: expandRowOnClick
        };
      },
      onPageChange: this.props.onPageChange,
      onSortedChange: (reactTableSortedList) => {
        const forServer = reactTableSortedList.map(reactTableSortedProp => {
          const r = {};
          const index = +reactTableSortedProp.id.split("-")[1];
          const key = values[index][1];

          if (key === DISABLE_SORT) { return { blank: true }; }

          const descOrAsc = reactTableSortedProp.desc ? "desc" : "asc";
          r[key] = descOrAsc;
          return r;
        }).filter(f => !f.blank);

        const wrapped = {
          forServer,
          reactTableSortedList
        };

        this.props.onSortedChange(wrapped);

        return false;
      },
      manual: true,
      defaultPageSize: pageSize,
      pages: totalPages,
      page: this.props.page,
      sorted: this.props.sorted.reactTableSortedList,
      expanded: this.state.expanded
    };

    return (
      <div className="parameter-detail">
        <BasicReactTableWrapper
          data={tableData}
          minWidths={minWidths}
          unsortableColumns={unsortableColumns}
          labelColumnIndex={(i) => i !== indexOfValue}
          reactTableProps={reactTableProps}
          decimals={false}
          subComponent={subComponent}
        />
      </div>
    );
  }
}

StandardsList.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  onSortedChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sorted: PropTypes.shape({
    reactTableSortedList: PropTypes.array.isRequired
  }),
  totalResults: PropTypes.number.isRequired,
  standards: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  regulationFocused: PropTypes.bool.isRequired,
  parameterFocused: PropTypes.bool.isRequired
};

export default StandardsList;
